import dayjs from 'dayjs';

/**
 * Safely parse a date string that might be in various formats
 * Common formats: DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, ISO strings
 */
export function parseDate(dateString: string): Date | null {
  if (!dateString || dateString.trim() === '') {
    return null;
  }

  try {
    // First try standard Date parsing (handles ISO format YYYY-MM-DD well)
    let parsedDate = new Date(dateString);

    // If the standard parsing worked, return it
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate;
    }

    // If that fails, try common Indian date formats
    if (dateString.includes('/') || dateString.includes('-')) {
      const separator = dateString.includes('/') ? '/' : '-';
      const parts = dateString.split(separator);

      if (parts.length === 3) {
        const [first, second, third] = parts.map((p) => parseInt(p.trim()));

        // Try DD/MM/YYYY format (common in India)
        if (first <= 31 && second <= 12 && third > 31) {
          parsedDate = new Date(third, second - 1, first);
        }
        // Try MM/DD/YYYY format
        else if (first <= 12 && second <= 31 && third > 31) {
          parsedDate = new Date(third, first - 1, second);
        }
        // Try YYYY/MM/DD or YYYY-MM-DD format (already handled above, but as fallback)
        else if (first > 31 && second <= 12 && third <= 31) {
          parsedDate = new Date(first, second - 1, third);
        }
      }
    }

    // Check if the parsed date is valid
    if (isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  } catch (error) {
    return null;
  }
}

/**
 * Format a date for display in Indian locale
 * Returns "-" if date is null or invalid
 * Returns "Invalid Date" if the date cannot be parsed
 * Example output: "31/12/2023"
 */
export function formatDateForDisplay(date: Date | string | null): string {
  if (!date) return '-';

  const formatted = parseDate2(typeof date === 'string' ? date : date.toISOString());

  return formatted || 'Invalid Date';
}

/**
 * Check if a date string is valid
 */
export function isValidDate(dateString: string): boolean {
  return parseDate2(dateString) !== null;
}

/**
 * Get the financial year range for a given year
 * Financial year in India runs from April 1 to March 31
 * @param year - The year for which to get the financial year range
 * @returns Object with start and end dates of the financial year
 */
export function getFinancialYearRange(year: number): {
  start: Date;
  end: Date;
  label: string;
} {
  // Create dates at midnight local time to avoid timezone issues
  const startDate = new Date(year, 3, 1, 0, 0, 0, 0); // April 1 (month 3, 0-indexed)
  const endDate = new Date(year + 1, 2, 31, 23, 59, 59, 999); // March 31 (month 2, 0-indexed) end of day

  return {
    start: startDate,
    end: endDate,
    label: `${year}-${year + 1}`,
  };
}

/**
 * Get current financial year details
 * @returns Current financial year information
 */
export function getCurrentFinancialYear(): {
  start: Date;
  end: Date;
  label: string;
  year: number;
} {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  // If current month is Jan, Feb, or Mar, we're in the previous calendar year's financial year
  const financialYear = currentMonth < 3 ? currentYear - 1 : currentYear;

  const fyRange = getFinancialYearRange(financialYear);

  return {
    ...fyRange,
    year: financialYear,
  };
}

/**
 * Check if a date falls within a specific financial year
 * @param date - The date to check
 * @param financialYear - The financial year (e.g., "2025-2026" or 2025)
 * @returns boolean indicating if the date is within the financial year
 */
export function isDateInFinancialYear(
  date: Date | string,
  financialYear: string | number,
): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) return false;

  let year: number;
  if (typeof financialYear === 'string') {
    // Extract year from "2025-2026" format
    year = parseInt(financialYear.split('-')[0]);
  } else {
    year = financialYear;
  }

  const fyRange = getFinancialYearRange(year);

  return dateObj >= fyRange.start && dateObj <= fyRange.end;
}

/**
 * Get default date range for reports based on financial year
 * @param financialYear - The financial year (e.g., "2025-2026" or 2025)
 * @returns Object with fromDate and toDate
 */
export function getFinancialYearDateRange(financialYear: string | number): {
  fromDate: string;
  toDate: string;
} {
  let year: number;
  if (typeof financialYear === 'string') {
    year = parseInt(financialYear.split('-')[0]);
  } else {
    year = financialYear;
  }

  const currentFY = getCurrentFinancialYear();
  const currentDate = new Date();

  // If it's the current financial year, use current date as end date
  if (year === currentFY.year) {
    return {
      fromDate: formatDateForAPI(currentFY.start),
      toDate: formatDateForAPI(currentDate),
    };
  } else {
    // For past financial years, use the full range
    const fyRange = getFinancialYearRange(year);
    return {
      fromDate: formatDateForAPI(fyRange.start),
      toDate: formatDateForAPI(fyRange.end),
    };
  }
}

/**
 * Format a date for API consumption (YYYY-MM-DD format)
 * @param date - The date to format
 * @returns Formatted date string
 */
export function formatDateForAPI(date: Date | string | null): string {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (!dateObj || isNaN(dateObj.getTime())) return '';

  // Use local date to avoid timezone issues
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Format a date for display in Indian format (DD/MM/YYYY)
 * @param date - The date to format
 * @returns Formatted date string for display
 */
export function formatDateForDisplayFromAPI(apiDate: string): string {
  if (!apiDate) return '-';

  return parseDate2(apiDate) || 'Invalid Date';
}

/**
 * Validate if a date is within allowed financial year range
 * @param date - The date to validate
 * @param allowedFinancialYear - The allowed financial year
 * @returns Validation result with error message if invalid
 */
export function validateDateInFinancialYear(
  date: Date | string,
  allowedFinancialYear: string | number,
): {
  isValid: boolean;
  errorMessage?: string;
} {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (!dateObj || isNaN(dateObj.getTime())) {
    return {
      isValid: false,
      errorMessage: 'Invalid date format',
    };
  }

  if (!isDateInFinancialYear(dateObj, allowedFinancialYear)) {
    let year: number;
    if (typeof allowedFinancialYear === 'string') {
      year = parseInt(allowedFinancialYear.split('-')[0]);
    } else {
      year = allowedFinancialYear;
    }

    const fyRange = getFinancialYearRange(year);

    return {
      isValid: false,
      errorMessage: `Date must be within financial year ${fyRange.label} (${formatDateForDisplay(
        fyRange.start,
      )} to ${formatDateForDisplay(fyRange.end)}). Please change the financial year to select this date.`,
    };
  }

  return {
    isValid: true,
  };
}

/**
 * Debug helper function to test financial year calculations
 * Can be called from console to check what dates are being generated
 */
export function debugFinancialYear(year?: number) {
  const currentFY = getCurrentFinancialYear();
  console.log('Current Financial Year:', currentFY);

  const testYear = year || currentFY.year;
  const fyRange = getFinancialYearRange(testYear);
  console.log(`Financial Year ${testYear} Range:`, fyRange);

  const dateRange = getFinancialYearDateRange(testYear);
  console.log(`Date Range for FY ${testYear}:`, dateRange);

  return { currentFY, fyRange, dateRange };
}

/**
 * Debug function to test date parsing in browser console
 * Usage: window.testDateParsing('2023-04-01')
 */
export function testDateParsing(dateString: string) {
  console.log(`Testing date parsing for: "${dateString}"`);

  const formatted = parseDate2(dateString);
  console.log('Formatted result:', formatted);

  if (formatted) {
    console.log('Formatted for display:', formatDateForDisplay(dateString));
    console.log('Formatted for API:', formatDateForAPI(dateString));

    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) {
      console.log('Month:', parsed.getMonth() + 1, 'Year:', parsed.getFullYear());
    }
  }

  return formatted;
}

export function parseDate2(date: string) {
  const mDate = dayjs(date);
  return mDate.isValid() ? mDate.format('DD/MM/YYYY') : null;
}

export const getFinancialYear = () => {
  const today = new Date();
  const year = today.getFullYear();
  const isAfterMarch = today.getMonth() >= 3; // Months are 0-indexed (0 = January, 3 = April)

  const financialYear = isAfterMarch ? year : year - 1;

  return financialYear;
};

export function getFinancialYearDates(startYear: string | number) {
  startYear = Number(startYear);
  const fromDate = new Date(startYear, 3, 1); // April 1 of the start year
  const currentDate = new Date(); // today's date
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  let toDate;

  // Check if we're in the same financial year range (April to March)
  if (currentYear === startYear && currentMonth >= 3) {
    // Between April and March of the same year range
    toDate = currentDate;
  } else if (currentYear === startYear + 1 && currentMonth < 3) {
    // January to March of the next year still belongs to the previous financial year
    toDate = currentDate;
  } else {
    // If it's beyond March of the next year, set to the end of the financial year
    toDate = new Date(startYear + 1, 2, 31); // March 31 of the next year
  }
  return {
    from_date: fromDate.toLocaleDateString('en-GB'), // Format as DD/MM/YYYY
    to_date: toDate.toLocaleDateString('en-GB'),
  };
}

/**
 * Formats a period string nicely.
 * Examples:
 * - "Mar 20169m" -> "Mar 2016 (9M)"
 * - "Mar 2016 (9M)" -> "Mar 2016 (9M)"
 * - "Mar 2016" -> "Mar 2016"
 * - "mar 201612m" -> "Mar 2016 (12M)"
 */
export function formatPeriod(label: string): string {
  if (!label) return '';
  const trimmed = label.trim();
  if (!trimmed) return trimmed;

  // Supports formats like "Mar 20169m", "Mar 2016 (9M)", "Mar 2016", "Mar 2016 12M"
  const regex = /^([A-Za-z]{3})\s*(\d{4})\s*(?:\(?\s*(\d+)\s*[mM]\)?\s*)?$/;
  const match = trimmed.match(regex);
  if (match) {
    const month = match[1];
    const year = match[2];
    const suffixNum = match[3];
    const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
    if (suffixNum) {
      return `${capitalizedMonth} ${year} (${suffixNum}M)`;
    }
    return `${capitalizedMonth} ${year}`;
  }
  return trimmed;
}

const PERIOD_MONTH_RANK: Record<string, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

/**
 * Ranks a period label for chronological sorting.
 * Supports formats like "Mar 20169m", "Mar 2016 (9M)", "Mar 2016", and handles "TTM" correctly.
 */
export function getPeriodRank(label: string): number {
  if (!label) return Number.MAX_SAFE_INTEGER;
  const normalizedLabel = label.trim();
  if (!normalizedLabel) {
    return Number.MAX_SAFE_INTEGER;
  }

  if (normalizedLabel.toUpperCase() === 'TTM') {
    return Number.MAX_SAFE_INTEGER - 1;
  }

  // Regex supporting "Mar 20169m", "Mar 2016 (9M)", "Mar 2016"
  const regex = /^([A-Za-z]{3})\s*(\d{4})\s*(?:\(?\s*(\d+)\s*[mM]\)?\s*)?$/;
  const match = normalizedLabel.match(regex);
  if (match) {
    const month = PERIOD_MONTH_RANK[match[1].toLowerCase()] ?? 0;
    const year = Number(match[2]);
    if (Number.isFinite(year)) {
      return year * 100 + month;
    }
  }

  const parsedDate = Date.parse(normalizedLabel);
  if (!Number.isNaN(parsedDate)) {
    return parsedDate;
  }

  return Number.MAX_SAFE_INTEGER - 2;
}
